import { Button } from "@dropbox/dig-components/buttons";
import { Modal } from "@dropbox/dig-components/modal";
import { Text, Title } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { AddLine } from "@dropbox/dig-icons/dist/mjs/assets";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Select_StacksAction } from "@mirage/analytics/events/types/select_stacks_action";
import { PAP_Shown_DashEmptyStateComponent } from "@mirage/analytics/events/types/shown_dash_empty_state_component";
import { PAP_View_DashStack } from "@mirage/analytics/events/types/view_dash_stack";
import {
  createUxaElementId,
  dispatchElementClicked,
} from "@mirage/analytics/uxa";
import { listPublishedContent, unpublishStack } from "@mirage/service-stacks";
import { stackDerivePAPProps } from "@mirage/service-stacks/service/utils";
import { DashSpinnerAutofitContainer } from "@mirage/shared/dash-spinner/DashSpinnerAutofitContainer";
import { useIsMobileSize } from "@mirage/shared/responsive/mobile";
import { showSnackbar } from "@mirage/shared/snackbar";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import { publishedContentsAtom } from "@mirage/stacks/hooks";
import { isPublishedContentsLoading } from "@mirage/stacks/hooks/useInitPublishContentsData";
import i18n from "@mirage/translations";
import classnames from "classnames";
import { useAtom, useAtomValue } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./ListPublishedStacks.module.css";
import { PublishedContentEmptyState } from "./PublishedContentEmptyState";
import PublishedContentListTable from "./PublishedContentTable";
import { PublishStacksModal } from "./PublishStacksModal";

import type { stacks } from "@dropbox/api-v2-client";

const MAX_ALLOWED_PUBLISHED_STACKS = 20;

export const ListPublishedStacks = () => {
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [stackToUnpublish, setStackToUnpublish] = useState<
    stacks.Stack | undefined
  >(undefined);

  const isLoading = useAtomValue(isPublishedContentsLoading);
  const isMobile = useIsMobileSize();

  const [publishedContents, setPublishedContents] = useAtom(
    publishedContentsAtom,
  );

  const [disablePublishButton, setDisablePublishButton] = useState(false);

  useEffect(() => {
    setDisablePublishButton(
      publishedContents.length > MAX_ALLOWED_PUBLISHED_STACKS,
    );
  }, [publishedContents]);

  const data = useMemo(() => {
    return publishedContents;
  }, [publishedContents]);

  const onClickUnPublish = useCallback((stack: stacks.Stack) => {
    // display confirmation dialog
    setShowConfirmationModal(true);
    setStackToUnpublish(stack);
  }, []);

  const onUnpublishConfirm = useCallback(async () => {
    setShowConfirmationModal(false);
    if (!stackToUnpublish || !stackToUnpublish.sharing_id) {
      return;
    }
    showSnackbar({
      title: i18n.t("unpublish_stack_inprogress", {
        stackName: stackToUnpublish.stack_data?.name,
      }),
      timeoutMs: 0,
    });
    try {
      await unpublishStack(stackToUnpublish?.sharing_id);
      const listResponse = await listPublishedContent();
      setPublishedContents(listResponse || []);
      showSnackbar({
        title: i18n.t("unpublish_stack_success", {
          stackName: stackToUnpublish.stack_data?.name,
        }),
      });
      reportPapEvent(
        PAP_Select_StacksAction({
          ...stackDerivePAPProps(stackToUnpublish),
          stacksAction: "unpublish_dash_stack",
          dashActionSurface: "published_content_page",
          featureLine: "stacks",
          actionSurfaceComponent: "published_content",
        }),
      );
    } catch {
      showSnackbar({
        title: i18n.t("unpublish_stack_failure", {
          stackName: stackToUnpublish.stack_data?.name,
        }),
      });
    }
    setStackToUnpublish(undefined);
  }, [stackToUnpublish]);

  const navigate = useNavigate();
  const { reportPapEvent } = useMirageAnalyticsContext();

  const onSelectStack = useCallback(
    (stack: stacks.Stack) => {
      reportPapEvent(
        PAP_View_DashStack({
          actionSurfaceComponent: "published_content",
          ...stackDerivePAPProps(stack),
          featureLine: "stacks",
          dashActionSurface: "published_content_page",
        }),
      );
      dispatchElementClicked(
        createUxaElementId("stack", {
          featureLine: "stacks",
        }),
      );
      navigate(`/stacks/${stack.sharing_id}`);
    },
    [navigate, reportPapEvent],
  );

  const onCloseModal = useCallback(() => {
    setShowConfirmationModal(false);
    setStackToUnpublish(undefined);
  }, []);

  const handleShownEmptyState = useCallback(() => {
    reportPapEvent(
      PAP_Shown_DashEmptyStateComponent({
        dashActionSurface: "published_content_page",
        actionSurfaceComponent: "published_content",
      }),
    );
  }, [reportPapEvent]);

  return (
    <div>
      <div
        className={classnames(styles.subtitleContainer, {
          [styles.isMobile]: isMobile,
        })}
      >
        <Text>{i18n.t("published_content_subtitle")}</Text>
        {disablePublishButton ? (
          <DigTooltip
            title={i18n.t("publish_button_disable_text", {
              count: MAX_ALLOWED_PUBLISHED_STACKS,
            })}
            placement="top"
          >
            <Box
              as="span"
              display="inline-block"
              borderRadius="Medium"
              outlineWidth="0"
              tabIndex={0}
            >
              <Button
                variant="primary"
                withIconStart={<UIIcon src={AddLine} />}
                disabled
                style={{
                  pointerEvents: "none",
                }}
              >
                {i18n.t("publish_button_text")}
              </Button>
            </Box>
          </DigTooltip>
        ) : (
          <Button
            variant="primary"
            onClick={() => setShowPublishModal(true)}
            withIconStart={<UIIcon src={AddLine} />}
          >
            {i18n.t("publish_button_text")}
          </Button>
        )}
      </div>

      {data.length > 0 ? (
        <PublishedContentListTable
          content={data}
          onClickUnpublish={onClickUnPublish}
          onSelectStack={onSelectStack}
        />
      ) : isLoading ? (
        <DashSpinnerAutofitContainer />
      ) : (
        <PublishedContentEmptyState
          text={i18n.t("published_content_empty_state_text")}
          onShownEmptyState={handleShownEmptyState}
        />
      )}
      {showConfirmationModal && (
        <ConfirmUnpublishModal
          onClose={onCloseModal}
          onUnpublishConfirm={onUnpublishConfirm}
          open={showConfirmationModal}
        />
      )}
      {showPublishModal && (
        <PublishStacksModal
          open={showPublishModal}
          onClose={() => setShowPublishModal(false)}
        />
      )}
    </div>
  );
};

type Props = {
  open: boolean;
  onClose: () => void;
  onUnpublishConfirm: () => void;
};

export function ConfirmUnpublishModal({
  open,
  onClose,
  onUnpublishConfirm,
}: Props) {
  return (
    <Modal
      open={open}
      onRequestClose={onClose}
      isCentered
      withCloseButton={i18n.t("close")}
    >
      <Modal.Header>
        <Title>{i18n.t("unpublish_stack_confirm_title")}</Title>
      </Modal.Header>
      <Modal.Body>
        <Text>{i18n.t("unpublish_stack_confirm_subtitle")}</Text>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="opacity" onClick={onClose}>
          {i18n.t("cancel")}
        </Button>
        <Button variant="primary" onClick={onUnpublishConfirm}>
          {i18n.t("unpublish")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
